import {
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { Box, Typography } from "@mui/material";
import { Amplify } from "aws-amplify";
import { fetchAuthSession, getCurrentUser, signOut } from "aws-amplify/auth";
import { Hub } from "aws-amplify/utils";
import { amplifyConfig } from "../../configs/cognito";
import { ApiContext } from "../app/context";
import { useWhoAmIQuery } from "../../services/api/__generated__/backend_gateway-types";
import { AuthenticationService as BgwAuthenticationService } from "../../services/api/authentication";
import { AuthenticationService as PAuthenticationService } from "../../services/perspective_api/authentication";
import { AuthenticationContainer } from "../../components/authentication/container";
import { AuthenticationLoading } from "../../components/authentication/loading";
import {
  createFreshIdTokenGetter,
  IdTokenContext,
  isTerminalAuthSessionError,
} from "./id_token_context";

export { IdTokenContext } from "./id_token_context";
export type { IdTokenProviderType } from "./id_token_context";

// Configure Amplify
console.log("Configuring Amplify with config:", amplifyConfig);
Amplify.configure(amplifyConfig);

const { Provider: IdTokenProvider } = IdTokenContext;

const Unauthorized = () => {
  const handleAuthenticationSuccess = () => {
    // The authentication context will handle the state change
    // and re-render the component tree
    // Use a longer delay to ensure the auth state is updated, especially for challenge flows
    setTimeout(() => {
      window.location.reload();
    }, 1000);
  };

  return (
    <AuthenticationContainer
      onAuthenticationSuccess={handleAuthenticationSuccess}
    />
  );
};

export const AmplifyAuthContent = ({ children }: { children: ReactNode }) => {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);
  const [hasIdToken, setHasIdToken] = useState<boolean>(false);
  const [idToken, setIdToken] = useState<string | undefined>(undefined);
  const idTokenRef = useRef<string | undefined>(undefined);
  const sessionExpiredRef = useRef(false);
  const { apiService } = useContext(ApiContext);

  const publishIdToken = useCallback((token: string) => {
    sessionExpiredRef.current = false;
    idTokenRef.current = token;
    setIdToken(token);
    BgwAuthenticationService.setIdToken(token);
    PAuthenticationService.setIdToken(token);
    setHasIdToken(true);
  }, []);
  const markSessionExpired = useCallback(() => {
    if (sessionExpiredRef.current) return;
    sessionExpiredRef.current = true;
    idTokenRef.current = undefined;
    setIdToken(undefined);
    BgwAuthenticationService.setIdToken(undefined);
    PAuthenticationService.setIdToken(undefined);
    setHasIdToken(false);
    setIsAuthenticated(false);
    setIsLoading(false);
    setError(null);
    const reportSignOutFailure = (signOutError: unknown) => {
      console.warn("Could not clear the expired Amplify session", signOutError);
    };
    try {
      void signOut().catch(reportSignOutFailure);
    } catch (signOutError) {
      reportSignOutFailure(signOutError);
    }
  }, []);
  const getFreshIdToken = useMemo(
    () =>
      createFreshIdTokenGetter({
        getCurrentToken: () => idTokenRef.current,
        fetchSession: fetchAuthSession,
        publishToken: publishIdToken,
        onSessionExpired: markSessionExpired,
      }),
    [markSessionExpired, publishIdToken],
  );

  const {
    data: whoAmIData,
    //loading: whoAmILoading,
    //error: whoAmIError,
  } = useWhoAmIQuery({
    client: apiService.getClient(),
    skip: !isAuthenticated,
  });

  const getToken = useCallback(async () => {
    try {
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();

      if (token) {
        publishIdToken(token);
        console.info("Tokens have been set");
      } else {
        console.warn("No ID token found in session");
        markSessionExpired();
      }
    } catch (e) {
      console.error("Error getting token:", e);
      if (isTerminalAuthSessionError(e)) {
        markSessionExpired();
      } else {
        setError(e as Error);
        setHasIdToken(false);
      }
    }
  }, [markSessionExpired, publishIdToken]);

  const checkAuth = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null); // Clear any previous errors
      console.log("Checking authentication...");
      const user = await getCurrentUser();
      console.log("User found:", user);
      if (user) {
        setIsAuthenticated(true);
        await getToken();
      } else {
        setIsAuthenticated(false);
      }
    } catch (err) {
      // getCurrentUser() throws UserUnAuthenticatedException when no user is logged in
      // This is expected behavior, not an error
      console.log(
        "User not authenticated (expected if no user logged in):",
        err,
      );
      setIsAuthenticated(false);
      setError(null); // Don't treat this as an error
    } finally {
      setIsLoading(false);
    }
  }, [getToken]);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // After a Hosted-UI/Google redirect, Amplify exchanges the `?code=...` for
  // tokens asynchronously, AFTER this component has already mounted and run
  // the checkAuth() above - so that first check can run before the exchange
  // finishes, see "not authenticated" and leave the user stuck on the login
  // screen even though sign-in succeeds a moment later. Hub is Amplify's own
  // notification for when that exchange actually completes (or fails), so
  // re-run the same checkAuth() from it rather than only the one-shot mount
  // check - this reuses getToken()/publishIdToken() exactly as a normal
  // sign-in does, it doesn't fetch a second token in parallel.
  useEffect(() => {
    const unsubscribe = Hub.listen("auth", ({ payload }) => {
      if (
        payload.event === "signInWithRedirect" ||
        payload.event === "signInWithRedirect_failure"
      ) {
        if (isAuthenticated) {
          // Amplify fires signInWithRedirect_failure on ANY ?error=
          // callback, including rejecting an *additional* Google account
          // linked while already signed in. checkAuth() sets isLoading=true
          // while it runs, which unmounts/remounts the entire authenticated
          // `children` tree (in-app state, perspective-viewer sockets, all
          // of it) - unnecessary when the existing session is already known
          // good and nothing about it changed. Skip the re-check entirely.
          return;
        }
        // Re-check rather than assuming the outcome from the event alone:
        // on a genuine failure with no prior session, it resolves to
        // not-authenticated anyway, and falling through to the
        // unauthenticated branch is what renders LandingComponent - the
        // thing that reads ?error=/error_description= off the URL and shows
        // the right message (including NOT_INVITED).
        checkAuth();
      }
    });
    return unsubscribe;
  }, [checkAuth, isAuthenticated]);

  // Set up token refresh callback
  useEffect(() => {
    const handleTokenRefresh = (newToken: string) => {
      console.info("Token refreshed in AmplifyAuthContent");
      publishIdToken(newToken);
    };

    BgwAuthenticationService.setOnTokenRefresh(handleTokenRefresh);
    // Registry, not the single slot: this must not evict the perspective
    // service's socket-rotation listener (or be evicted by a view's).
    const removePerspectiveListener =
      PAuthenticationService.addOnTokenRefresh(handleTokenRefresh);

    return () => {
      BgwAuthenticationService.setOnTokenRefresh(() => {});
      removePerspectiveListener();
    };
  }, [publishIdToken]);

  useEffect(() => {
    if (isAuthenticated && whoAmIData?.whoAmI?.email) {
      // heap.identify(whoAmIData.whoAmI.email);
    }
  }, [isAuthenticated, whoAmIData]);

  if (isLoading) {
    return <AuthenticationLoading />;
  }

  if (error) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh",
          background: "#fafafa",
          padding: 2,
        }}
      >
        <Box sx={{ textAlign: "center", color: "#333333" }}>
          <Typography variant="h5" gutterBottom>
            Authentication Error
          </Typography>
          <Typography variant="body1">{error.message}</Typography>
        </Box>
      </Box>
    );
  }

  if (!isAuthenticated) {
    return (
      <BrowserRouter>
        <Routes>
          <Route path="*" element={<Unauthorized />} />
        </Routes>
      </BrowserRouter>
    );
  }

  if (!hasIdToken) {
    return <AuthenticationLoading />;
  }

  return (
    <IdTokenProvider value={{ idToken, getFreshIdToken, markSessionExpired }}>
      {children}
    </IdTokenProvider>
  );
};

export const AmplifyAuthProvider = ({ children }: { children: ReactNode }) => {
  return <AmplifyAuthContent>{children}</AmplifyAuthContent>;
};
